import { HttpClient } from '@shared/adapters/HttpClient';
import { CreateEntryAPIData, UpdateEntryAPIData } from '../CreateEdit/models/CreateEntrySchema';
import { Entry, EntryApiResponse, EntryFilters, EntryReceipt } from '../models';

/**
 * Construye los parámetros de query para advanced_filters
 * Formato: advanced_filters[filters][0][column]=..., advanced_filters[logic]=...
 */
function buildAdvancedFiltersParams(advancedFilters: {
  filters: Array<{ column: string; operator: string; value?: any }>;
  logic: string;
}): Record<string, any> {
  const params: Record<string, any> = {};

  if (advancedFilters.filters && Array.isArray(advancedFilters.filters)) {
    advancedFilters.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (f.value !== undefined && f.value !== null && !(Array.isArray(f.value) && f.value.length === 0)) {
        // Si es un array, enviar como valores separados
        if (Array.isArray(f.value)) {
          f.value.forEach((v: any, j: number) => {
            params[`advanced_filters[filters][${i}][value][${j}]`] = v;
          });
        } else {
          params[`advanced_filters[filters][${i}][value]`] = f.value;
        }
      }
    });
  }

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
}

export class EntryApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene la lista paginada de entradas
   */
  async getEntries(filters?: EntryFilters): Promise<EntryApiResponse> {
    const params: Record<string, any> = {};

    if (filters) {
      // Paginación
      if (filters.page) params.page = String(filters.page);
      if (filters.per_page) params.per_page = String(filters.per_page);

      // Ordenamiento
      if (filters.sorting) {
        params['sorting[column]'] = filters.sorting.column;
        params['sorting[direction]'] = filters.sorting.direction;
      }

      // Búsqueda simple
      if (filters.searchTerm) {
        params.search = filters.searchTerm;
      }

      // Filtros avanzados - usar formato de query params anidados
      if (filters.advanced_filters && filters.advanced_filters.filters.length > 0) {
        const advancedFiltersParams = buildAdvancedFiltersParams(filters.advanced_filters);
        Object.assign(params, advancedFiltersParams);
      }

      // product_ids se envía como param directo (ej: product_ids=919,920,921)
      if (filters.product_ids && filters.product_ids.length > 0) {
        params.product_ids = filters.product_ids.join(',');
      }
    }

    return await this.httpClient.get<EntryApiResponse>('/entries', params);
  }

  /**
   * Obtiene una entrada específica por ID
   */
  async getEntry(id: number): Promise<Entry> {
    return await this.httpClient.get<Entry>(`/entries/${id}`);
  }

  /**
   * Crea una nueva entrada
   */
  async createEntry(data: CreateEntryAPIData): Promise<{ data: Entry }> {
    return await this.httpClient.post<{ data: Entry }>('/entries', data);
  }

  /**
   * Crea un albarán de recepción para una entrada
   */
  async createReceipt(entryId: number): Promise<{ data: EntryReceipt }> {
    return await this.httpClient.post<{ data: EntryReceipt }>(`/entries/${entryId}/receipts`, {});
  }

  /**
   * Obtiene un albarán específico por ID
   */
  async getReceipt(entryId: number, receiptId: number): Promise<EntryReceipt> {
    return await this.httpClient.get<EntryReceipt>(`/entries/${entryId}/receipts/${receiptId}`);
  }

  /**
   * Actualiza una entrada existente
   * Nota: La API puede retornar 204 No Content, en cuyo caso retorna undefined
   */
  async updateEntry(id: number, data: UpdateEntryAPIData): Promise<{ data: Entry } | undefined> {
    return await this.httpClient.put<{ data: Entry } | undefined>(`/entries/${id}`, data);
  }

  /**
   * Cancela una entrada pendiente
   * Nota: La API retorna 204 No Content
   */
  async cancelEntry(id: number): Promise<void> {
    await this.httpClient.put<void>(`/entries/${id}/cancel`, {});
  }
}
